'use client';

import Link from '@/components/i18n/LocaleLink';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { ArrowRight, Sparkles, Users, Eye, CreditCard, HelpCircle, X } from 'lucide-react';
import type { Lang, SectionVisibility } from '../types';
import type { TranslationKeys } from '../translations';
import { langNames, langFlags } from '../lang-meta';

interface MobileSidebarProps {
  t: TranslationKeys;
  lang: Lang;
  sections: SectionVisibility;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (v: boolean) => void;
  onLangChange: (l: Lang) => void;
}

export default function MobileSidebar({
  t,
  lang,
  sections,
  mobileMenuOpen,
  setMobileMenuOpen,
  onLangChange,
}: MobileSidebarProps) {
  return (
    <>
      {/* Mobile Sidebar Overlay */}
      <div
        className={`fixed inset-0 bg-black/60 z-[100] md:hidden transition-opacity duration-300 ${mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Sidebar */}
      <div className={`fixed top-0 right-0 h-full w-72 bg-purple-100 z-[110] transform transition-transform duration-300 ease-in-out md:hidden ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex flex-col h-full p-6">
          {/* Sidebar Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2">
              <Image src="/images/brand/ezdocuai-logo.png" alt="EZDocuAI" width={32} height={32} className="h-8 w-8" />
              <span className="text-xl font-semibold text-gray-900">EZDocuAI</span>
            </div>
            <button
              onClick={() => setMobileMenuOpen(false)}
              aria-label={t.menuClose}
              className="p-2 rounded-lg hover:bg-purple-200 transition-colors cursor-pointer"
            >
              <X className="h-5 w-5 text-gray-700" />
            </button>
          </div>

          {/* Navigation Links */}
          <nav className="flex-1 space-y-2">
            {sections.showFeatures && (
            <button
              onClick={() => {
                const el = document.getElementById('features');
                if (el) {
                  const top = el.getBoundingClientRect().top + window.scrollY - 80;
                  window.scrollTo({ top, behavior: 'smooth' });
                }
                setMobileMenuOpen(false);
              }}
              className="w-full text-left text-gray-700 hover:text-gray-900 py-3 px-4 rounded-xl hover:bg-purple-200 transition-colors flex items-center gap-3 cursor-pointer"
            >
              <Sparkles className="h-5 w-5 text-purple-600" />
              {t.features}
            </button>
            )}
            <button
              onClick={() => {
                const el = document.getElementById('built-for');
                if (el) {
                  const top = el.getBoundingClientRect().top + window.scrollY - 80;
                  window.scrollTo({ top, behavior: 'smooth' });
                }
                setMobileMenuOpen(false);
              }}
              className="w-full text-left text-gray-700 hover:text-gray-900 py-3 px-4 rounded-xl hover:bg-purple-200 transition-colors flex items-center gap-3 cursor-pointer"
            >
              <Users className="h-5 w-5 text-purple-600" />
              {t.builtForBadge}
            </button>
            {sections.showHowItWorks && (
            <button
              onClick={() => {
                const el = document.getElementById('how-it-works');
                if (el) {
                  const top = el.getBoundingClientRect().top + window.scrollY - 80;
                  window.scrollTo({ top, behavior: 'smooth' });
                }
                setMobileMenuOpen(false);
              }}
              className="w-full text-left text-gray-700 hover:text-gray-900 py-3 px-4 rounded-xl hover:bg-purple-200 transition-colors flex items-center gap-3 cursor-pointer"
            >
              <Eye className="h-5 w-5 text-purple-600" />
              {t.howItWorks}
            </button>
            )}
            {sections.showPricing && (
            <button
              onClick={() => {
                const el = document.getElementById('pricing');
                if (el) {
                  const top = el.getBoundingClientRect().top + window.scrollY - 80;
                  window.scrollTo({ top, behavior: 'smooth' });
                }
                setMobileMenuOpen(false);
              }}
              className="w-full text-left text-gray-700 hover:text-gray-900 py-3 px-4 rounded-xl hover:bg-purple-200 transition-colors flex items-center gap-3 cursor-pointer"
            >
              <CreditCard className="h-5 w-5 text-purple-600" />
              {t.pricing}
            </button>
            )}
            {sections.showFaq && (
            <button
              onClick={() => {
                const el = document.getElementById('faq');
                if (el) {
                  const top = el.getBoundingClientRect().top + window.scrollY - 80;
                  window.scrollTo({ top, behavior: 'smooth' });
                }
                setMobileMenuOpen(false);
              }}
              className="w-full text-left text-gray-700 hover:text-gray-900 py-3 px-4 rounded-xl hover:bg-purple-200 transition-colors flex items-center gap-3 cursor-pointer"
            >
              <HelpCircle className="h-5 w-5 text-purple-600" />
              {t.faq}
            </button>
            )}
          </nav>

          {/* Language Selector for Mobile */}
          <div className="py-4 border-t border-purple-200">
            <div className="flex items-center gap-1">
              {(['en', 'es', 'pt', 'de', 'fr'] as Lang[]).map((l) => (
                <button
                  key={l}
                  onClick={() => onLangChange(l)}
                  className={`flex-1 py-2 px-1 rounded-lg text-xs flex items-center justify-center gap-1 transition-colors cursor-pointer ${
                    lang === l ? 'bg-purple-600 text-white' : 'text-gray-600 hover:bg-purple-200'
                  }`}
                >
                  <span className="text-sm leading-none">{langFlags[l]}</span>
                  <span>{langNames[l]}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Bottom Actions */}
          <div className="space-y-3 pt-4 border-t border-purple-200">
            <Button asChild variant="outline" className="w-full bg-white border-purple-300 text-gray-700 hover:bg-purple-50 rounded-xl h-11">
              <Link href="/sign-in" onClick={() => setMobileMenuOpen(false)}>
                {t.login}
              </Link>
            </Button>
            <Button asChild className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-xl h-11 font-semibold">
              <Link href="/sign-up" onClick={() => setMobileMenuOpen(false)}>
                {t.getStarted}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
